// Canonical card class tokens — always use these instead of inline Tailwind strings.
// Import what you need: cn(cardBase, "p-4 overflow-hidden")

// Primary card — main content panels, section containers
export const cardBase = "rounded-xl border border-border bg-card shadow-glow";

// Surface card — secondary panels, stat tiles, sidebar blocks (bg-surface = slightly darker than bg-card)
export const cardSurface = "rounded-lg border border-border bg-surface";

// Subtle card — de-emphasized list rows, nested panels, proposal items
export const cardSubtle = "rounded-lg border border-border/40 bg-card";

// Status/alert cards — semantic colour pairs
export const cardSuccess = "rounded-lg border border-success/30 bg-success/5";
export const cardDestructive =
  "rounded-lg border border-destructive/40 bg-card";
export const cardPrimary = "rounded-lg border border-primary/30 bg-primary/5";

// Accent-rail disclosure card — a card whose left border is a colored "rail" marking open state
// or a fixed per-item accent. Used by `AccentRailDisclosure` (components/ui/accent-rail-disclosure);
// import that component rather than writing these Tailwind strings at a call site.
export const accentRailCardSubtle = `${cardSubtle} overflow-hidden`;
export const accentRailCardMuted =
  "rounded-lg border border-border bg-muted/20 transition-colors";

// Interactive module card — nav/situation cards with radial glow overlay
export const moduleCardBase =
  "group relative overflow-hidden rounded-xl border border-border bg-card dark:bg-surface shadow-glow transition-all duration-300";

export const moduleCardHover =
  "hover:border-accent/50 hover:shadow-[0_0_0_1px_color-mix(in_oklab,var(--accent)_35%,transparent),0_18px_48px_-14px_color-mix(in_oklab,var(--accent)_50%,transparent),0_8px_28px_-10px_color-mix(in_oklab,var(--primary)_30%,transparent)]";

export const moduleCard = `${moduleCardBase} ${moduleCardHover}`;

/** Pause/warn wash on top of `moduleCard`. Light uses a 5% warning tint on white. Dark must
 *  set `dark:bg-warning-subtle` — an unprefixed `bg-warning/5` loses to `moduleCard`'s
 *  `dark:bg-surface`, so a paused tile looks identical to a running one. */
export const moduleCardPaused =
  "border-warning/50 bg-warning/5 dark:border-warning/70 dark:bg-warning-subtle";

// Input highlight — always-on gradient border (same style as chat/report prompts)
export const inputGlowWrap =
  "relative rounded-xl p-[1.5px] bg-gradient-accent shadow-glow";
export const inputGlowInner = "rounded-[10px] bg-card";

// ── Status / KPI card tones ──────────────────────────────────────────────────
// Semantic tone for the value / status text on status cards (ConfigStatusTabs, capability
// status, KPI tiles). Always use these — never a raw `text-success` / `text-warning` for the
// value text: those are the mid-tone *fill* hues and read too light as text on a white ground.
// The `*-subtle-foreground` tokens are theme-tuned (dark hue on light, light hue on dark), so a
// green/amber status stays legible in both themes. Tone is SEMANTIC and never the accent hue
// (accent marks interactivity/selection, not good/bad).
//
// `learning` marks an in-progress state — a capability working toward readiness (a parameter still
// learning), distinct from `empty` ("nothing here"). It reuses the existing `info-subtle` tokens
// (hue 230, blue): clear of success/warning, and neither accent nor primary, so it reads as status,
// not interactivity — exactly what a semantic tone must.
export type CardTone = "ok" | "warn" | "empty" | "learning";

// Readable value/status TEXT.
export const cardToneText: Record<CardTone, string> = {
  ok: "text-success-subtle-foreground",
  warn: "text-warning-subtle-foreground",
  empty: "text-muted-foreground",
  learning: "text-info-subtle-foreground",
};

// Small status DOT (a fill, so the brighter base hue is fine).
export const cardToneDot: Record<CardTone, string> = {
  ok: "bg-success",
  warn: "bg-warning",
  empty: "bg-muted-foreground",
  learning: "bg-info",
};

// Pill (text + subtle border + subtle fill) — e.g. a capability/signal status chip.
export const cardTonePill: Record<CardTone, string> = {
  ok: "text-success-subtle-foreground border-success/40 bg-success/5",
  warn: "text-warning-subtle-foreground border-warning/40 bg-warning/5 dark:border-warning/60 dark:bg-warning/20",
  empty: "text-muted-foreground border-border bg-transparent",
  learning: "text-info-subtle-foreground border-info/40 bg-info/5",
};
